/**
 * My allowance — how much of this month's AI allowance the signed-in person
 * has used, as percentages only. The API answers for the caller alone.
 */

import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';

interface Usage {
  percent: number;
  paused: boolean;
}

interface MyAllowanceInfo {
  month: string;
  /** Null when not used this month or last. */
  claude: Usage | null;
  codex: Usage | null;
  checked_at: string | null;
  resets_on: string;
}

function day(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y!, m! - 1, d).toLocaleDateString(undefined, { day: 'numeric', month: 'long' });
}

function barColour(percent: number): string {
  if (percent >= 100) return 'bg-red-600';
  if (percent >= 80) return 'bg-orange-500';
  if (percent >= 50) return 'bg-amber-400';
  return 'bg-navy-600';
}

function UsageCard({ title, note, usage, resetsOn }: {
  title: string;
  note: string;
  usage: Usage;
  resetsOn: string;
}): ReactNode {
  return (
    <section className="rounded-lg border border-steel-200 bg-white p-5" aria-label={title}>
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-navy-900">{title}</h3>
          <p className="text-xs text-steel-500">{note}</p>
        </div>
        <p className="text-2xl font-semibold text-navy-900">{usage.percent}%</p>
      </div>
      <div className="mt-3 h-2 rounded-full bg-steel-100" role="progressbar"
        aria-valuenow={Math.min(usage.percent, 100)} aria-valuemin={0} aria-valuemax={100}>
        <div className={`h-2 rounded-full ${barColour(usage.percent)}`}
          style={{ width: `${Math.min(usage.percent, 100)}%` }} />
      </div>
      {usage.paused ? (
        <p className="mt-3 text-sm text-red-700">
          Paused until {day(resetsOn)}. Contact IT if you need more for your work.
        </p>
      ) : (
        <p className="mt-3 text-sm text-steel-600">{usage.percent}% of this month's allowance used.</p>
      )}
    </section>
  );
}

export function MyAllowance(): ReactNode {
  const query = useQuery({
    queryKey: ['my-allowance'],
    queryFn: () => api.get<MyAllowanceInfo>('/me/allowance'),
  });

  return (
    <div className="flex max-w-3xl flex-col gap-5 animate-fade-in" data-testid="my-allowance-page">
      <header>
        <h2 className="text-xl font-semibold text-navy-900">My allowance</h2>
        <p className="mt-1 text-sm text-steel-600">
          How much of this month's AI allowance you've used. It's based on the AWS
          bill, which can be up to a day behind. Only you can see this page's figures.
        </p>
      </header>
      {query.isLoading && <p className="text-sm text-steel-500">Loading…</p>}
      {query.isError && <p className="text-sm text-rose-600">Couldn't load your allowance.</p>}
      {query.data && (
        <>
          <div className="grid gap-4 md:grid-cols-2">
            {query.data.claude && (
              <UsageCard title="Claude" note="Desktop, Cowork and this portal"
                usage={query.data.claude} resetsOn={query.data.resets_on} />
            )}
            {query.data.codex && (
              <UsageCard title="Codex" note="Separate from Claude"
                usage={query.data.codex} resetsOn={query.data.resets_on} />
            )}
          </div>
          <p className="text-xs text-steel-500">
            Last checked {query.data.checked_at ? new Date(query.data.checked_at).toLocaleString() : 'not yet'}.
            Resets on {day(query.data.resets_on)}.
          </p>
        </>
      )}
    </div>
  );
}

export default MyAllowance;
